import { Box, styled } from '@mui/material';
import type { Node } from '@xyflow/react';
import { LwFormSelect } from 'redesign';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { SidebarFooter } from 'ogp/components/views/management/workflows/components/sidebar-footer';
import { ActionNodeFormValues } from './action-node-editor.types';
import {
  actionTypeContentLibrary,
  actionTypeOptions,
  actionTypePayloadConfig,
} from './action-node-editor.config';

export const ActionNodeEditor = ({
  node,
  onSave,
  onClose,
}: {
  node: Node;
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) => {
  const source = (node.data ?? {}) as Partial<ActionNodeFormValues>;
  const form = useForm<ActionNodeFormValues>({
    defaultValues: {
      actionType: source.actionType,
      partnerIds: source.partnerIds,
    },
  });

  const selectedActionType = useWatch({
    control: form.control,
    name: 'actionType',
  });

  const ActionTypeContentComponent = selectedActionType
    ? actionTypeContentLibrary[selectedActionType]
    : undefined;

  const submit = form.handleSubmit((values) => {
    const actionTypePayload = actionTypePayloadConfig[values.actionType]?.(values) ?? {};

    onSave({
      ...(node.data ?? {}),
      actionType: values.actionType,
      ...actionTypePayload,
    });
    onClose();
  });

  return (
    <FormProvider {...form}>
      <EditorWrapper>
        <ContentWrapper>
          <Title>Action</Title>
          <LwFormSelect
            name="actionType"
            label="Action type"
            control={form.control}
            options={actionTypeOptions}
            defaultLabel="Select action"
            rules={{ required: true }}
          />
          {ActionTypeContentComponent && <ActionTypeContentComponent formControl={form.control} />}
        </ContentWrapper>
        <SidebarFooter onClose={onClose} submit={submit} />
      </EditorWrapper>
    </FormProvider>
  );
};

const EditorWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const ContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  flex: 1,
  overflowY: 'auto',
  padding: '12px 4px',
});

const Title = styled(Box)({
  fontWeight: 700,
  fontSize: 18,
  textAlign: 'center',
  marginBottom: 12,
  marginTop: 12,
});
